/**
 * Feature flag utilities
 * Centralized management of release feature flags
 *
 * In production, PostHog feature flags take precedence.
 * Falls back to local constants in non-production environments.
 *
 * For React components, you can also use PostHog's useFeatureFlagEnabled hook
 * for automatic re-renders when flags change.
 */

import {
  getFeatureFlag,
  isFeatureEnabled as checkFeatureEnabled,
} from '../services/analyticsService'

/**
 * Subscriptions feature flag
 * Set to false to disable subscription/pricing features
 * In production, PostHog feature flag 'subscriptions_enabled' takes precedence
 */
export const SUBSCRIPTIONS_ENABLED = false

/**
 * Check if subscriptions feature is enabled
 * In production, checks PostHog feature flag first
 */
export const isSubscriptionsEnabled = (): boolean => {
  // Check PostHog feature flag first (only works in production)
  const posthogFlag = getFeatureFlag('subscriptions_enabled')
  if (typeof posthogFlag === 'boolean') {
    return posthogFlag
  }

  // Fall back to local constant
  return SUBSCRIPTIONS_ENABLED
}

/**
 * Sponsorships feature flag
 * Controls visibility of the Sponsorships menu and related features
 * Set VITE_FEATURE_SPONSORSHIPS=true to enable (default: false)
 * In production, PostHog feature flag 'sponsorships_enabled' takes precedence
 */
export const SPONSORSHIPS_ENABLED = import.meta.env.VITE_FEATURE_SPONSORSHIPS === 'true'

/**
 * Check if sponsorships feature is enabled
 * In production, checks PostHog feature flag first
 */
export const isSponsorshipsEnabled = (): boolean => {
  // Check PostHog feature flag first (only works in production)
  const posthogFlag = getFeatureFlag('sponsorships_enabled')
  if (typeof posthogFlag === 'boolean') {
    return posthogFlag
  }

  // Fall back to environment variable (default: false)
  return SPONSORSHIPS_ENABLED
}

/**
 * Brand Scout feature flag
 * Controls visibility of the Brand Scout menu and related features
 * Set VITE_FEATURE_BRAND_SCOUT=true to enable (default: false)
 * In production, PostHog feature flag 'brand_scout_enabled' takes precedence
 */
export const BRAND_SCOUT_ENABLED = import.meta.env.VITE_FEATURE_BRAND_SCOUT === 'true'

/**
 * Check if Brand Scout feature is enabled
 * In production, checks PostHog feature flag first
 */
export const isBrandScoutEnabled = (): boolean => {
  const posthogFlag = getFeatureFlag('brand_scout_enabled')
  if (typeof posthogFlag === 'boolean') return posthogFlag
  return BRAND_SCOUT_ENABLED
}

export const BROADCAST_ANALYSIS_ENABLED =
  import.meta.env.VITE_FEATURE_BROADCAST_ANALYSIS === 'true'

/**
 * Broadcast analysis feature flag — menu visibility only.
 *
 * Access is gated separately by the `broadcast_analysis` ORGANISATION feature
 * on the auth service, which the API enforces. This flag just decides whether
 * the nav entry is drawn, so turning it on does not grant anyone anything.
 */
export const isBroadcastAnalysisEnabled = (): boolean => {
  const posthogFlag = getFeatureFlag('broadcast_analysis_enabled')
  if (typeof posthogFlag === 'boolean') return posthogFlag
  return BROADCAST_ANALYSIS_ENABLED
}

/**
 * Instagram Graph API feature flag
 * Controls visibility of Instagram connection settings and enhanced analytics
 * Set VITE_FEATURE_INSTAGRAM_GRAPH_API=true to enable (default: false)
 * In production, PostHog feature flag 'instagram_graph_api_enabled' takes precedence
 */
// Default true — set VITE_FEATURE_INSTAGRAM_GRAPH_API=false to disable
export const INSTAGRAM_GRAPH_API_ENABLED = import.meta.env.VITE_FEATURE_INSTAGRAM_GRAPH_API !== 'false'

/**
 * Check if Instagram Graph API feature is enabled at the deploy level.
 * PostHog flag 'instagram_graph_api_enabled' takes precedence in production.
 * Defaults to true — set to false in PostHog or via env var to hide all Enhanced API UI.
 */
export const isInstagramGraphApiEnabled = (): boolean => {
  const posthogFlag = getFeatureFlag('instagram_graph_api_enabled')
  if (typeof posthogFlag === 'boolean') return posthogFlag
  return INSTAGRAM_GRAPH_API_ENABLED
}

/**
 * Newsletter Dashboard feature flag
 * Controls visibility of the Newsletter Dashboard menu
 * Set VITE_FEATURE_NEWSLETTER=true to enable (default: false)
 * In production, PostHog feature flag 'newsletter_enabled' takes precedence
 */
export const NEWSLETTER_ENABLED = import.meta.env.VITE_FEATURE_NEWSLETTER === 'true'

/**
 * Check if Newsletter Dashboard feature is enabled
 * In production, checks PostHog feature flag first
 */
export const isNewsletterEnabled = (): boolean => {
  const posthogFlag = getFeatureFlag('newsletter_enabled')
  if (typeof posthogFlag === 'boolean') return posthogFlag
  return NEWSLETTER_ENABLED
}

/**
 * Generic feature flag check using PostHog
 *
 * @param flagKey - The feature flag key defined in PostHog
 * @param defaultValue - Value to use when flag is not available
 */
export const isFeatureEnabled = (
  flagKey: string,
  defaultValue = false
): boolean => {
  const posthogFlag = checkFeatureEnabled(flagKey)
  return posthogFlag ?? defaultValue
}
